import React from "react";
import { Box, Button, Typography } from "@mui/material";
import { useNavigate } from "react-router";
import { useTranslation } from "react-i18next";
import { format } from "date-fns";
import { Dividend } from "@bn/api/apiv1";
import { TableConfig } from "@components";
import { toCurrency } from "@helpers";

interface Args {
  investmentId: number;
  onEditDividend: (bookingId: number) => void;
}

export const useDividendsTableConfig = ({
  investmentId,
  onEditDividend,
}: Args): TableConfig<Dividend> => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const createLink = (date: string) => {
    navigate(
      `/investments/${investmentId}/dividend-wizard/create?date=${date}`
    );
  };

  return {
    id: "investmentDividends",
    showHeader: true,
    getRowId: (row) => row.bookingId ?? row.date ?? "",
    columns: [
      {
        id: "date",
        title: t("investment.dividends.dividendsTableHeaders.date"),
        align: "left",
        renderer: ({ row }) => (
          <Box display="flex">
            <Typography fontWeight="bold">
              {row.date ? format(new Date(row.date), "dd.MM.yyyy") : ""}
            </Typography>
          </Box>
        ),
      },
      {
        id: "dividendPerShare",
        title: t("investment.dividends.dividendsTableHeaders.dividend"),
        align: "left",
        renderer: ({ row }) => (
          <Typography variant="body1">
            {toCurrency(row.dividendPerShare, row.currency)}
          </Typography>
        ),
      },
      {
        id: "numberOfLots",
        title: t("investment.dividends.dividendsTableHeaders.lots"),
        align: "left",
        renderer: ({ row }) => (
          <Typography variant="body1">{row.numberOfLots ?? 0}</Typography>
        ),
      },
      {
        id: "grossAmount",
        title: t("investment.dividends.dividendsTableHeaders.total"),
        align: "left",
        renderer: ({ row }) => (
          <Typography variant="body1">
            {toCurrency(row.grossAmount, row.currency)}
          </Typography>
        ),
      },
      {
        id: "actions",
        title: t("investment.dividends.dividendsTableHeaders.actions"),
        align: "right",
        renderer: ({ row }) => {
          const disabled = row.numberOfLots === 0;

          if (row.bookingId) {
            return (
              <Button
                variant="text"
                color="primary"
                disabled={disabled}
                onClick={() => onEditDividend(row.bookingId!)}
              >
                {t("investment.dividends.tableActions.editDividends")}
              </Button>
            );
          }

          return (
            <Button
              variant="text"
              color="primary"
              disabled={disabled}
              onClick={() =>
                row.date
                  ? createLink(format(new Date(row.date), "MM-dd-yyyy"))
                  : null
              }
            >
              {t("investment.dividends.tableActions.addDividends")}
            </Button>
          );
        },
      },
    ],
  };
};
